import { ArrowLeft, House, RotateCw } from 'lucide-react';

import mangrovesUrl from '@/assets/illustrations/error/mangroves.svg';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import { config } from '@/config';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

import type { ErrorStatus } from '@/services/api/errorStatus';

const SUPPORT_EMAIL = 'support@nabat.ai';
// The design's buttons are 44px tall, between the m and l scales.
const BUTTON_CLASS = 'h-11 font-button';
// Box shadows on each panel: `drop-shadow()` takes a single shadow, and chaining six compounds them.
const PANEL_SHADOW =
  'shadow-[0_100px_93.5px_rgba(0,0,0,0.01),0_41.778px_39.062px_rgba(0,0,0,0.01),0_22.336px_20.884px_rgba(0,0,0,0.01),0_12.522px_11.708px_rgba(0,0,0,0.01),0_6.65px_6.218px_rgba(0,0,0,0.01),0_2.767px_2.587px_rgba(0,0,0,0.02)]';
const ICON_PROPS = { size: 24, strokeWidth: 1.2, 'aria-hidden': true } as const;

interface ErrorCopy {
  eyebrow: string;
  title: string;
  description: string;
  /** Whether the secondary action retries (the failure may be transient) or goes back. */
  retry: boolean;
}

const COPY: Record<ErrorStatus, ErrorCopy> = {
  'not-found': {
    eyebrow: 'Error 404',
    title: 'Page not found',
    description:
      "The page you're looking for doesn't exist or has been moved. Check the address for typos, or head to the home page.",
    retry: false,
  },
  forbidden: {
    eyebrow: 'Error 403',
    title: "You don't have access",
    description:
      'You do not have access to this page. Ask an admin for access, or head to the home page.',
    retry: false,
  },
  server: {
    eyebrow: 'Server error',
    title: 'Something went wrong on our end',
    description:
      "We couldn't load this page. Try again in a moment, and if it keeps happening, let us know.",
    retry: true,
  },
  offline: {
    eyebrow: 'Connection lost',
    title: "You're offline",
    description: "We couldn't reach NabatOS. Check your internet connection and try again.",
    retry: true,
  },
  unknown: {
    eyebrow: 'Unexpected error',
    title: 'Something went wrong',
    description:
      'An unexpected error stopped this page from loading. Try again, or head to the home page.',
    retry: true,
  },
};

interface ErrorPageProps {
  status: ErrorStatus;
  /** The HTTP status, shown in place of the eyebrow for server errors (e.g. "Error 503"). */
  code?: number;
  /** Replaces the default retry (a full reload), e.g. to reset an error boundary. */
  onRetry?: () => void;
  /** Developer detail, shown below the description in dev builds only. */
  detail?: string;
}

/**
 * Full-page error state. Navigates with plain browser APIs rather than the
 * router, so it also works as the top-level error boundary's fallback.
 */
export function ErrorPage({ status, code, onRetry, detail }: ErrorPageProps) {
  const { t } = useTranslation();
  const copy = COPY[status];
  const eyebrow = status === 'server' && code ? t('Error {code}', { code }) : t(copy.eyebrow);
  const reportHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `${eyebrow}: ${window.location.pathname}`
  )}`;

  return (
    // The body doesn't scroll (the app is a full-screen map), so the page scrolls itself.
    <main className="h-dvh overflow-y-auto bg-surface-highlight-light">
      <div className="flex min-h-full items-center justify-center px-xxl py-12 sm:px-16">
        <div className="flex">
          {/* The SVG carries the panel's brand fill and its rounded left corners. It stretches
              (cover) with the card when longer copy or a translation makes it taller. */}
          <img
            src={mangrovesUrl}
            alt=""
            aria-hidden
            width={299}
            height={407}
            className={cn(
              'hidden h-auto shrink-0 rounded-bl-[25.835px] rounded-tl-[60px] object-cover md:block',
              PANEL_SHADOW
            )}
          />

          <div
            className={cn(
              'flex items-center rounded-br-[60px] rounded-tl-[60px] bg-surface-highlight p-12 md:rounded-tl-none',
              PANEL_SHADOW
            )}
          >
            <div className="flex w-full max-w-[440px] flex-col gap-xxxl">
              <div className="flex flex-col gap-md">
                <Typography
                  variant="label"
                  as="p"
                  textColor="brand"
                  className="font-body uppercase leading-5 tracking-[0.08em]"
                >
                  {eyebrow}
                </Typography>
                <Typography variant="h4" as="h1" className="font-title">
                  {t(copy.title)}
                </Typography>
                <Typography textColor="secondary" className="font-body leading-6">
                  {t(copy.description)}
                </Typography>
                {import.meta.env.DEV && detail && (
                  <pre className="whitespace-pre-wrap rounded bg-surface-muted p-md text-xs">
                    {detail}
                  </pre>
                )}
              </div>

              <div className="flex flex-col gap-xxl">
                <div className="flex gap-md">
                  {copy.retry ? (
                    <Button
                      appearance="transparent"
                      kind="neutral"
                      scale="l"
                      width="full"
                      className={BUTTON_CLASS}
                      iconStart={<RotateCw {...ICON_PROPS} />}
                      onClick={onRetry ?? (() => window.location.reload())}
                    >
                      {t('Try again')}
                    </Button>
                  ) : (
                    <Button
                      appearance="transparent"
                      kind="neutral"
                      scale="l"
                      width="full"
                      className={BUTTON_CLASS}
                      iconStart={<ArrowLeft {...ICON_PROPS} />}
                      onClick={() => window.history.back()}
                    >
                      {t('Go back')}
                    </Button>
                  )}
                  <Button
                    asChild
                    appearance="solid"
                    kind="inverse"
                    scale="l"
                    width="full"
                    className={BUTTON_CLASS}
                    iconStart={<House {...ICON_PROPS} />}
                  >
                    <a href={config.app.basename}>{t('Home')}</a>
                  </Button>
                </div>

                <hr className="border-outline" />

                <div className="flex items-center justify-center gap-sm">
                  <Typography variant="body-small" as="span" className="font-body leading-[22px]">
                    {t('Think something is broken?')}
                  </Typography>
                  <Button
                    asChild
                    appearance="transparent"
                    kind="brand"
                    scale="s"
                    className="h-auto px-0 py-xxs font-button text-[length:var(--text-size--1)] leading-[22px] hover:bg-transparent hover:underline"
                  >
                    <a href={reportHref}>{t('Report a problem')}</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
